import styled from "styled-components";
import { Link } from "react-router-dom";
import Button from "../../../../library/Button";
import { tokens } from "../../../../../public/tokens";

/*
 * Fills its own grid column (VersionCardsGrid.js) rather than a fixed px width, so 3
 * columns evenly split the full-width row with no fixed-track gutter. `align-self: start`
 * stops the grid's default row-stretch from forcing this card to match a taller sibling's
 * height - min-height: 285px is the floor, content decides the rest.
 */
export const StyledCard = styled.article({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignSelf: "start",
    width: "100%",
    minWidth: 0,
    maxWidth: "none",
    minHeight: "285px",
    gap: tokens.globals["spacing-2-5"],
    padding: tokens.globals["spacing-3-5"],
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
});

export const StyledTop = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-2-5"],
});

export const StyledHeaderRow = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.globals["spacing-2"],
});

export const StyledName = styled.span({
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["xl-18px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: 1.1,
});

export const StyledBadges = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
});

const statusStyles = {
    active: { background: tokens.colors.states.success["100"], foreground: tokens.colors.states.success["500"] },
    inactive: { background: tokens.colors.neutral.grey.Alpha["10"], foreground: tokens.colors.text.secondary },
    locked: { background: tokens.colors.states.warning["500"], foreground: tokens.colors.neutral.white },
    unlocked: { background: tokens.colors.neutral.grey.Alpha["10"], foreground: tokens.colors.text.secondary },
};

export const StyledBadge = styled.span(({ $variant = "active" }) => {
    const style = statusStyles[$variant] || statusStyles.active;

    return {
        minHeight: "18px",
        padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`,
        borderRadius: tokens.globals["border-radius"],
        display: "inline-flex",
        alignItems: "center",
        gap: tokens.globals["spacing-1"],
        background: style.background,
        color: style.foreground,
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontSize: tokens.typography.fontSize["sm-10px"],
        fontWeight: tokens.typography.fontWeights.semibold,
        lineHeight: 1,
        whiteSpace: "nowrap",

        i: { fontSize: tokens.typography.fontSize["xs-8px"] },
    };
});

export const StyledCoordinatorRow = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
});

/* Matches Avatar's own 28px circular footprint, so the "Not Assigned" row lines up with the assigned one - a warning icon, not a fabricated person/initials. */
export const StyledCoordinatorPlaceholder = styled.span({
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: tokens.colors.states.warning["100"],
    color: tokens.colors.states.warning["500"],
    fontSize: tokens.typography.fontSize["md-12px"],
});

export const StyledCoordinatorInfo = styled.span({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-0-5"],
    minWidth: 0,
});

export const StyledCoordinatorName = styled.span({
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "12px",
});

export const StyledCoordinatorRole = styled.span({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "normal",
});

export const StyledCounters = styled.div({
    display: "flex",
    alignItems: "stretch",
    justifyContent: "space-between",
    gap: tokens.globals["spacing-2"],
    padding: `${tokens.globals["spacing-1-5"]} ${tokens.globals["spacing-2-5"]}`,
    background: tokens.colors.ui.accent,
    borderRadius: tokens.globals["border-radius"],
});

export const StyledCounter = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-0-5"],
});

export const StyledCounterLabel = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    whiteSpace: "nowrap",

    i: { fontSize: tokens.typography.fontSize["lg-14px"] },
});

export const StyledCounterValue = styled.span({
    paddingLeft: tokens.globals["spacing-5"],
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
});

/*
 * Note text + its "by {author} · {timestamp}" attribution are one sub-group (4px gap),
 * distinct from the card's own ~10px major gap between top-level content blocks.
 */
export const StyledNoteGroup = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-1"],
    width: "100%",
});

export const StyledNotes = styled.p({
    margin: 0,
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontWeight: tokens.typography.fontWeights.regular,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    lineHeight: "normal",
});

export const StyledNoteAttribution = styled.p({
    margin: 0,
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["xs-8px"],
    fontWeight: tokens.typography.fontWeights.regular,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    lineHeight: "normal",
});

export const StyledDatesRow = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1"],
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["sm-10px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],

    i: { marginRight: tokens.globals["spacing-1"] },
});

export const StyledDateDivider = styled.span({
    width: tokens.globals["spacing-px"],
    alignSelf: "stretch",
    background: tokens.colors.surfaces["border-divider"],
});

export const StyledFooter = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
});

/*
 * `styled(Button)` combined with an `as={Link}` on the SAME instance would swap the
 * rendered element away from `Button` before its own variant styling ever runs (styled-
 * components resolves `as` at the layer where it's declared, bypassing the wrapped
 * component entirely) - that renders an unstyled bare link. So, matching the existing
 * StyledIconLink/IconButton pattern elsewhere in this codebase, the Link stays a plain
 * layout wrapper and `Button` is rendered normally (unswapped) as its child.
 */
export const StyledOpenLink = styled(Link)({
    flex: "1 0 0",
    display: "flex",
    textDecoration: "none",
});

export const StyledOpenButton = styled(Button)({
    width: "100%",
    height: "35px",
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
});
